import React, { useState } from 'react';
import styled from 'styled-components/native';
import { Ionicons } from '@expo/vector-icons';

import ArcProgressBar from '../ArcProgressBar';
import { P } from '../Typography';
import { Platform, useWindowDimensions } from 'react-native';
import elementIDs from '../../../elementIDs';

const Container = styled.View`
  align-self: center;
  align-items: center;
  justify-content: center;
  overflow: visible;
`;

const StyledLabelContainer = styled.View<{ size: number }>`
  position: absolute;
  align-self: center;
  width: ${p => p.size * 0.6}px;
  height: ${p => p.size * 0.4}px;
  align-items: center;
  justify-content: center;
`;

const StyledHelperTextContainer = styled.View`
  align-self: center;
  position: absolute;
  bottom: -15px;
  align-items: center;
`;

const StyledP = styled(P)`
  font-size: 12px;
  line-height: 14px;
  letter-spacing: -0.07px;
  text-transform: uppercase;
`;

interface GaugeChartProps {
  values: string[];
  size?: number;
  label?: string;
  helperText?: string;
  value: number;
}

function degToRad(deg: number) {
  return (deg * Math.PI) / 180;
}

const GaugeChart: React.FC<GaugeChartProps> = ({
  values,
  label,
  helperText,
  value,
  size = 200
}) => {
  const itemCount = values.length;
  const [textWidth, setTextWidth] = useState(
    Array.from(Array(itemCount).fill(0))
  );
  const newTextWidth = [...textWidth];
  const window = useWindowDimensions();
  return (
    <Container>
      <ArcProgressBar progress={value} max={1} size={size} />
      {Array.from(Array(itemCount), (_, index) => {
        const lastIndex = itemCount - 1;
        const finalNumOfItems = (itemCount - 1) * 2;
        const percentage = index === 0 ? 0 : index / finalNumOfItems;
        const angleRad = degToRad(480 * percentage + 150);
        const labelCircleSize = size + size * 0.25;
        const radius = labelCircleSize / 2;
        const rightAxis = index > lastIndex / 2;
        const middle = !(index < lastIndex / 2) && !rightAxis;
        const x = radius * Math.cos(angleRad) + radius;
        const y = radius * Math.sin(angleRad) + radius;
        const left =
          rightAxis && index === lastIndex
            ? x * 0.75 + 15
            : rightAxis
              ? x * 0.75 + 35
              : middle
                ? radius * 0.75 - textWidth[index] / 2
                : index === 0
                  ? x - textWidth[index] - 10
                  : x - textWidth[index] - 20;
        const StyledContainer = styled.View`
          opacity: ${textWidth[index] === 0 ? 0 : 1};
          position: absolute;
          top: ${y}px;
          left: ${window.width <= 380 ? Math.max(-62, left) : left}px;
          margin-top: ${-(middle
            ? 30
            : index !== 0 && index !== lastIndex
              ? size * 0.1
              : size * 0.17)}px;
          word-break: normal;
          ${Platform.OS !== 'web' && 'max-width: 76px'};
        `;
        const textPosition = left * 2.5 + textWidth[index];
        const numberOfLines = textPosition > window.width ? 2 : 1;
        return (
          <StyledContainer
            onLayout={event => {
              const { width } = event.nativeEvent.layout;
              if (textWidth[index] === 0 && width !== 0) {
                newTextWidth[index] = Math.floor(width);
                setTextWidth(newTextWidth);
              }
            }}
            key={index}
          >
            <P numberOfLines={numberOfLines} color='dark'>
              {values[index]}
            </P>
          </StyledContainer>
        );
      })}
      {!!label && (
        <StyledLabelContainer size={size}>
          <P
            testID={elementIDs.gaugeChartLabelID}
            style={{ textAlign: 'center' }}
            color='dark'
          >
            {label}
          </P>
        </StyledLabelContainer>
      )}
      {helperText && (
        <StyledHelperTextContainer>
          <Ionicons name='caret-up' size={18} color='black' />
          <StyledP color='dark'>{helperText}</StyledP>
        </StyledHelperTextContainer>
      )}
    </Container>
  );
};

export default GaugeChart;
